import type { IndicatorStatus } from '@movingimage-evp/mi-ui-component-library';

import type { GetWebcastsQuery, User, Webcast } from '../../../../generated/graphql-manager';

import { CheckCircleIcon, CloseIcon, ProgressIndicator, ProgressList } from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  GetWebcastsDocument as GET_WEBCASTS,
  State,
  UserStatus,
  useDuplicateWebcastMutation,
  useGetWebcastStateQuery,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { getUserNameAndInitials, keycloak } from '../../../../service/authentication/keycloak-provider';
import { addParamToPath } from '../../../../utils';
import { useCurrentUser } from '../../../hooks/current-user';

import styles from '../../../components/progress-controller/progress-controller.module.css';

type Props = {
  duplicatedWebcastId: Webcast['id'];
  duplicatedWebcastName: Webcast['name'];
  onWebcastDuplicated: () => void;
  onWebcastDuplicatedError: () => void;
};

export function DuplicationProgressIndicator({
  duplicatedWebcastId,
  duplicatedWebcastName,
  onWebcastDuplicated,
  onWebcastDuplicatedError,
}: Props) {
  const navigate = useNavigate();
  const { lsproId, userId } = useCurrentUser();
  const routes = useAbsoluteRoutes();
  const { t } = useTranslation();

  const [newWebcastId, setNewWebcastId] = useState('');
  const [duplicateWebcastMutation, { called }] = useDuplicateWebcastMutation();
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [error, setError] = useState(false);

  const {
    data: getWebcastResponse,
    startPolling,
    stopPolling,
  } = useGetWebcastStateQuery({
    variables: { webcastId: newWebcastId },
    skip: !newWebcastId,
  });

  const webcastDuplicated = getWebcastResponse?.webcast.state === 'PRELIVE';

  const duplicateWebcast = useCallback(() => {
    duplicateWebcastMutation({
      variables: { input: { id: duplicatedWebcastId } },
      update: (cache, { data }) => {
        if (data?.duplicateWebcast.__typename !== 'DuplicateWebcastSuccess') return;

        const cachedWebcastsQuery = cache.readQuery<GetWebcastsQuery>({
          query: GET_WEBCASTS,
          variables: { lsproId },
        });

        const originalWebcastData = cachedWebcastsQuery?.webcasts.edges.find(
          (edge) => edge.node.id === duplicatedWebcastId
        )?.node;

        const originalWebcastIndex = cachedWebcastsQuery?.webcasts.edges.findIndex(
          (edge) => edge.node.id === duplicatedWebcastId
        );

        if (cachedWebcastsQuery?.webcasts.edges && originalWebcastIndex !== undefined && originalWebcastData) {
          const { email } = getUserNameAndInitials();
          const user = keycloak?.tokenParsed;

          const currentUserAsCreator: User = {
            id: userId,
            email: email || '',
            firstName: user?.given_name || null,
            lastName: user?.family_name || null,
            invitedAt: null,
            roles: [],
            status: UserStatus.ACTIVE,
          };

          const newWebcastData = {
            __typename: 'WebcastEdge',
            node: {
              ...originalWebcastData,
              id: data.duplicateWebcast.webcast.id,
              state: State.INITIALIZING,
              streaming: null, // Don't carry over the streaming data so that the "On-Demand" badge is not shown
              name: `${originalWebcastData.name} ${t('manager.webcasts.duplicateWebcast.copy')}`,
              creator: currentUserAsCreator,
            },
          };

          cache.writeQuery({
            query: GET_WEBCASTS,
            variables: { lsproId },
            data: {
              webcasts: {
                ...cachedWebcastsQuery.webcasts,
                edges: [
                  ...cachedWebcastsQuery.webcasts.edges.slice(0, originalWebcastIndex + 1),
                  newWebcastData,
                  ...cachedWebcastsQuery.webcasts.edges.slice(originalWebcastIndex + 1),
                ],
              },
            },
          });
        }
      },
      onCompleted: (data) => {
        if (data.duplicateWebcast.__typename === 'DuplicateWebcastProblem') {
          setError(true);
          setProgressPercentage(100);
          onWebcastDuplicatedError();
        }

        if (data.duplicateWebcast.__typename === 'DuplicateWebcastSuccess') {
          setNewWebcastId(data.duplicateWebcast.webcast.id);
          setProgressPercentage(100);
        }
      },
    });
  }, [duplicateWebcastMutation, duplicatedWebcastId, lsproId, onWebcastDuplicatedError, t, userId]);

  useEffect(() => {
    if (called) return;

    duplicateWebcast();
  }, [called, duplicateWebcast]);

  useEffect(() => {
    if (webcastDuplicated) {
      onWebcastDuplicated();
      stopPolling();
      return;
    }

    startPolling(1000);
  }, [webcastDuplicated, onWebcastDuplicated, startPolling, stopPolling]);

  const progressIndicator = {
    INITIALIZING: {
      label: t('manager.webcasts.duplicateWebcast.progressIndicator.initializingLabel', {
        webcastName: duplicatedWebcastName,
      }),
      status: 'PROCESSING' as IndicatorStatus,
    },
    PRELIVE: {
      label: t('manager.webcasts.duplicateWebcast.progressIndicator.finishedLabel', {
        webcastName: duplicatedWebcastName,
      }),
      status: 'FINISHED' as IndicatorStatus,
      button: t('manager.webcasts.duplicateWebcast.progressIndicator.finishedButtonLabel'),
      onButtonClick: () => {
        navigate(addParamToPath(routes.webcastSetup_setupSummary, newWebcastId));
      },
      icon: <CheckCircleIcon style={{ fill: 'var(--mi-brand-main)' }} />,
    },
    POSTLIVE: {
      status: 'FINISHED' as IndicatorStatus,
    },
    LIVE: {
      status: 'FINISHED' as IndicatorStatus,
    },
    LIVE_TEST: {
      status: 'FINISHED' as IndicatorStatus,
    },
    ARCHIVED: {
      status: 'FINISHED' as IndicatorStatus,
    },
    FAILED: {
      label: t('manager.webcasts.duplicateWebcast.progressIndicator.errorLabel', {
        webcastName: duplicatedWebcastName,
      }),
      status: 'FAILED' as IndicatorStatus,
      button: t('manager.webcasts.duplicateWebcast.progressIndicator.errorButtonLabel'),
      onButtonClick: duplicateWebcast,
      icon: <CloseIcon style={{ fill: 'var(--mi-error-main)' }} />,
    },
  }[error ? 'FAILED' : getWebcastResponse?.webcast.state || 'INITIALIZING'];

  return (
    <ProgressList data-testid="duplication-progress-indicator" className={styles.progressList}>
      <ProgressIndicator
        progressPercentage={progressPercentage}
        status={progressIndicator.status}
        label={progressIndicator.label}
        icon={progressIndicator.icon}
        buttonLabel={progressIndicator.button}
        onButtonClicked={progressIndicator.onButtonClick}
      />
    </ProgressList>
  );
}
